/**
 * Search over the map: one box, three kinds of answer.
 *
 * The owner opened the tool and could not find the geocoder — because there was none. The box
 * took coordinates and said so, which is honest and half a tool: you are told «50.286, 31.313»
 * over the phone, but you are also told «Бзов, за элеватором».
 *
 * WHICH BRANCH IS TAKEN IS A PROPERTY OF THE STRING, NOT A MODE. No tabs, no toggle. If what
 * is typed parses as a pair (lib/coords.ts — twelve formats, DMS, Cyrillic hemispheres, a
 * pasted Google Maps link), it is a coordinate and NOT ONE REQUEST leaves the browser. That is
 * both the privacy answer and the reason the coordinate group is listed first: instant and free
 * above, networked below. Our parser catches ten formats more than the source's, so it also
 * keeps ten more kinds of nonsense out of a geocoder that would have guessed at them.
 *
 * A refused input says why and the map does not move. Flying to nowhere and leaving the
 * operator to work out that the pair was transposed is the failure worth avoiding here.
 *
 * WHY IT IS A CROSSHAIR AND NOT A PANEL. It used to be 288 × 82 px of the map's top edge,
 * always, including the third line that always had something in it — while the thing itself
 * is used for a few seconds on entering a district. Collapsed to 32 px, it costs the map
 * nothing when nobody is searching. It lives in the top-left corner group, between the contour
 * tools and the basemap switch (map/controls.ts), and the sheet opens downward over the imagery.
 *
 * AND THE MAIN ENTRANCE IS STILL NOT THE ICON. Paste is: `useCoordinatePaste` (hooks/) watches
 * for Ctrl+V outside a text field and flies there if — and only if — what was pasted parses as
 * a pair. That is ONE action. The sheet is for people who type.
 */

import { Fragment, useEffect, useId, useMemo, useRef, useState } from "react";

import { T } from "../strings";
import { formatPoint, parseCoordinates, type Point } from "../lib/coords";
import {
  formatPlaceLabel,
  placeTarget,
  type PhotonFeature,
  type SearchTarget,
} from "../lib/places";
import { MIN_QUERY_LENGTH, usePlaceSearch } from "../hooks/usePlaceSearch";
import type { Field } from "../api/types";
import "./CoordSearch.css";

/** Same ceiling as the geocoder's own: a list you have to scroll is a list you have to read. */
const MAX_FIELD_MATCHES = 5;

interface Props {
  onFound: (target: SearchTarget) => void;
  onClear: () => void;
  /** The point currently marked, if any — the mark is temporary and never a field. */
  marked: Point | null;
  /** Searched by substring, locally, for nothing: the owner types field names into this box. */
  fields: Field[];
  onSelectField: (fieldId: number) => void;
  /** `[lon, lat]` of the current view. Biases the geocoder; costs nothing, same one request. */
  near?: [number, number] | null;
}

type Item =
  | { kind: "coords"; point: Point }
  | { kind: "field"; field: Field }
  | { kind: "place"; feature: PhotonFeature };

const groupTitle = (kind: Item["kind"]): string => {
  if (kind === "coords") return T.coords.groupCoords;
  if (kind === "field") return T.coords.groupFields;
  return T.coords.groupPlaces;
};

export function CoordSearch({
  onFound,
  onClear,
  marked,
  fields,
  onSelectField,
  near = null,
}: Props) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [active, setActive] = useState(-1);
  // Escape closes the list before the sheet, so the list needs a "dismissed" of its own.
  const [listClosed, setListClosed] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const listId = useId();

  const place = usePlaceSearch(near);
  const { search: searchPlaces, clear: clearPlaces } = place;

  const query = value.trim();
  const parsed = useMemo(() => parseCoordinates(value), [value]);
  const isPair = parsed.ok;

  // Fields are filtered even when the string looks like a pair: a field can be called «50 га».
  const matchedFields = useMemo(() => {
    if (query === "") return [];
    const needle = query.toLowerCase();
    return fields
      .filter((field) => field.name.toLowerCase().includes(needle))
      .slice(0, MAX_FIELD_MATCHES);
  }, [fields, query]);

  const items = useMemo<Item[]>(() => {
    const out: Item[] = [];
    if (parsed.ok) out.push({ kind: "coords", point: parsed.point });
    for (const field of matchedFields) out.push({ kind: "field", field });
    for (const feature of place.results) out.push({ kind: "place", feature });
    return out;
  }, [parsed, matchedFields, place.results]);

  // A recognised pair silences the geocoder outright — this is the guard, and it is one line.
  useEffect(() => {
    if (!open || isPair || query.length < MIN_QUERY_LENGTH) {
      clearPlaces();
      return;
    }
    searchPlaces(query);
  }, [open, isPair, query, searchPlaces, clearPlaces]);

  // Opening the sheet is the act of wanting to type into it.
  useEffect(() => {
    if (open) input.current?.focus();
  }, [open]);

  useEffect(() => {
    if (active < 0) return;
    const row = list.current?.querySelectorAll("[data-item]")[active];
    // Guarded because jsdom has no `scrollIntoView`: the arrow keys must not depend on the
    // environment implementing a scroll nicety.
    if (row instanceof HTMLElement && typeof row.scrollIntoView === "function") {
      row.scrollIntoView({ block: "nearest" });
    }
  }, [active]);

  // Esc: first the list, then the sheet, then the mark. The list step is handled on the input
  // itself (it can stop the event before this sees it); the mark is temporary and this is how
  // it is said — it used to cost a permanent line of the panel to say it in words.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;
      if (open) {
        setOpen(false);
        setError(null);
      } else if (marked) {
        onClear();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, marked, onClear]);

  const hasTail = place.loading || place.failed || (query.length >= MIN_QUERY_LENGTH && !isPair);
  const listOpen = open && !listClosed && query !== "" && (items.length > 0 || hasTail);

  const reset = (): void => {
    setValue("");
    setError(null);
    setActive(-1);
    setOpen(false);
    clearPlaces();
  };

  const choose = (item: Item): void => {
    reset();
    if (item.kind === "field") {
      // No pin: a field is not a place the search invented. Selecting it is what flies the
      // map (MapCanvas fits the selected field), and it fills the console on the way.
      onSelectField(item.field.id);
      return;
    }
    onFound(item.kind === "coords" ? item.point : placeTarget(item.feature));
  };

  /**
   * The ladder. The source does nothing at all on Enter with nothing highlighted, which we
   * cannot afford: «Найти» is a button here, and pressing it has to answer something.
   */
  const submit = (event: React.FormEvent): void => {
    event.preventDefault();
    const highlighted = active >= 0 ? items[active] : undefined;
    if (highlighted) {
      choose(highlighted);
      return;
    }
    if (parsed.ok) {
      const point = parsed.point;
      reset();
      onFound(point);
      return;
    }
    const firstPlace = items.find((item) => item.kind === "place");
    if (firstPlace) {
      choose(firstPlace);
      return;
    }
    setError(parsed.error);
  };

  const onKeyDown = (event: React.KeyboardEvent): void => {
    if (event.key === "Escape" && listOpen) {
      event.preventDefault();
      // Stops the window handler below from closing the whole sheet in the same keystroke.
      event.stopPropagation();
      setListClosed(true);
      setActive(-1);
      return;
    }
    if (!listOpen || items.length === 0) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActive((current) => (current < items.length - 1 ? current + 1 : 0));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActive((current) => (current > 0 ? current - 1 : items.length - 1));
    }
  };

  return (
    <div className="k-coords">
      <button
        type="button"
        className={marked ? "k-iconbtn k-iconbtn--marked" : "k-iconbtn"}
        aria-expanded={open}
        aria-label={T.coords.label}
        title={T.coords.label}
        onClick={() => setOpen((current) => !current)}
      >
        {/* A crosshair: it points at a place. Fields are outlines, and nothing here should
            ever be mistaken for one. */}
        <svg
          viewBox="0 0 24 24"
          width="17"
          height="17"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="square"
          aria-hidden="true"
          focusable="false"
        >
          <path d="M12 3 L12 8 M12 16 L12 21 M3 12 L8 12 M16 12 L21 12" />
          <circle cx="12" cy="12" r="4.5" />
        </svg>
      </button>

      {open && (
        <form className="k-coords__sheet" onSubmit={submit} aria-label={T.coords.label}>
          <div className="k-coords__row">
            <input
              ref={input}
              className="k-input"
              value={value}
              // The placeholder carries both branches and the commonest format, which is what
              // the deleted third line was for. The full list of accepted formats is in the
              // refusal, where it is needed and where it is not standing chrome.
              placeholder={T.coords.placeholder}
              aria-label={T.coords.label}
              aria-autocomplete="list"
              aria-expanded={listOpen}
              aria-controls={listOpen ? listId : undefined}
              aria-activedescendant={active >= 0 ? `${listId}-${active}` : undefined}
              onKeyDown={onKeyDown}
              onChange={(event) => {
                setValue(event.target.value);
                setActive(-1);
                setListClosed(false);
                if (error) setError(null);
              }}
            />
            <button type="submit" className="k-btn">
              {T.coords.submit}
            </button>
          </div>

          {listOpen && (
            <div
              ref={list}
              id={listId}
              role="listbox"
              aria-label={T.coords.label}
              className="k-coords__list"
            >
              {items.map((item, index) => (
                <Fragment key={`${item.kind}-${index}`}>
                  {(index === 0 || items[index - 1]!.kind !== item.kind) && (
                    <div className="k-coords__group" role="presentation">
                      {groupTitle(item.kind)}
                    </div>
                  )}
                  <div
                    data-item
                    id={`${listId}-${index}`}
                    role="option"
                    aria-selected={index === active}
                    className={
                      index === active ? "k-coords__item k-coords__item--active" : "k-coords__item"
                    }
                    onMouseEnter={() => setActive(index)}
                    // The input must keep the focus: losing it mid-list would take the
                    // arrow keys away between hovering a row and clicking it.
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => choose(item)}
                  >
                    {item.kind === "coords" && (
                      <span className="k-coords__mono">{formatPoint(item.point)}</span>
                    )}
                    {item.kind === "field" && (
                      <>
                        <span className="k-coords__name">{item.field.name}</span>
                        <span className="k-coords__meta">
                          {T.coords.fieldArea(item.field.area_ha)}
                        </span>
                      </>
                    )}
                    {item.kind === "place" && (
                      <span className="k-coords__name">
                        {formatPlaceLabel(item.feature.properties)}
                      </span>
                    )}
                  </div>
                </Fragment>
              ))}

              {/* Three different sentences, because they have three different answers. Only
                  «недоступен» is about us; it also says what still works. */}
              {place.failed && (
                <div className="k-coords__note" role="status">
                  {T.coords.offline}
                </div>
              )}
              {!place.failed && place.loading && (
                <div className="k-coords__note" role="status">
                  {T.coords.searching}
                </div>
              )}
              {!place.failed && !place.loading && items.length === 0 && (
                <div className="k-coords__note" role="status">
                  {T.coords.nothing}
                </div>
              )}
            </div>
          )}

          {error && (
            <div className="k-note k-note--error" role="alert">
              {error}
            </div>
          )}

          {!error && marked && (
            <div className="k-coords__found">
              {/* Printed, not hover-only (coords-hint-visible): the swapped-pair check only
                  catches a latitude over 90°, so a reversed Russian pair (36.610, 55.097)
                  passes silently — the order hint under the mark is the one defence. Out of
                  `title` on purpose: a second identical text is a dupe, not a helper. */}
              <span>{T.coords.at(formatPoint(marked))}</span>
              <button
                type="button"
                className="k-btn k-btn--ghost"
                onClick={() => {
                  setValue("");
                  onClear();
                }}
              >
                {T.coords.clear}
              </button>
            </div>
          )}
          {!error && marked && <p className="k-caption">{T.coords.hint}</p>}
        </form>
      )}
    </div>
  );
}
